<script setup lang="ts">
/**
 * The favorite conversations as a row of avatars with their first names, above
 * a conversation list. `#add` is the last tile, for the caller's way of adding
 * one.
 */
import NxAvatar, { type NxAvatarStatus } from '../Avatar/NxAvatar.vue';
import type { NxChatAvatar } from './chatList';

export interface NxChatFavorite {
  id: string | number;
  name: string;
  /** Shown under the avatar; default: the first word of the name. */
  label?: string;
  avatar?: NxChatAvatar;
  status?: NxAvatarStatus;
}

withDefaults(
  defineProps<{
    favorites: readonly NxChatFavorite[];
    /** Logical px of the avatars. */
    avatarSize?: number;
    ariaLabel?: string;
  }>(),
  { avatarSize: 42, ariaLabel: 'Favoriten' }
);

const emit = defineEmits<{ select: [favorite: NxChatFavorite] }>();

function labelOf(favorite: NxChatFavorite): string {
  return favorite.label ?? favorite.name.split(' ')[0] ?? favorite.name;
}
</script>

<template>
  <ul class="nx-chat-favorites" :aria-label="ariaLabel" data-testid="nx-chat-favorites">
    <li v-for="favorite in favorites" :key="favorite.id">
      <button
        type="button"
        class="nx-chat-favorites__tile"
        :title="favorite.name"
        @click="emit('select', favorite)"
      >
        <NxAvatar
          :name="favorite.name"
          :size="avatarSize"
          :initials="favorite.avatar?.initials"
          :hue="favorite.avatar?.hue"
          :emphasis="favorite.avatar?.emphasis"
          :shape="favorite.avatar?.shape"
          :status="favorite.status"
          decorative
        />
        <span class="nx-chat-favorites__label">{{ labelOf(favorite) }}</span>
      </button>
    </li>
    <li v-if="$slots.add" class="nx-chat-favorites__add">
      <slot name="add" />
    </li>
  </ul>
</template>

<style scoped>
.nx-chat-favorites {
  display: flex;
  gap: var(--nxt-space-xs);
  margin: 0;
  padding: var(--nxt-space-xs) var(--nxt-space-md) var(--nxt-space-sm);
  list-style: none;
  overflow-x: auto;
  scrollbar-width: none;
}
.nx-chat-favorites__tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nxt-space-2xs);
  width: calc(var(--nxt-size-unit) * 3.5);
  padding: 0;
  border: 0;
  border-radius: var(--nxt-radius-md);
  background: none;
  color: var(--nxt-fg-muted);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  cursor: pointer;
}
.nx-chat-favorites__tile:hover {
  color: var(--nxt-fg);
}
.nx-chat-favorites__tile:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}
.nx-chat-favorites__label {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-chat-favorites__add {
  flex: none;
}
</style>
